import { AccessToken, Environment, MGMAuth, TokenParams } from '@mgmresorts/mgm-auth-js-sdk';
import { LogLevel } from '@mgmresorts/mgm-client-logging';
import React, { createContext, useEffect, useMemo, useCallback, useContext, useState } from 'react';

import { OktaUser } from '../../../@types/okta-user';
import { FlagsProvider } from '../../common/context/FlagsContext';
import { useAppRedirect } from '../../hooks/useAppRedirect';
import { useContent } from '../../hooks/useContent';
import { ClientId } from '../constants';
import { getFeaturesByClientId } from '../getFeaturesByClientId';
import { getThirdPartyScopesByClientId } from '../getScopes';
import { linkAccountToPartner } from '../linkAccountToPartner';
import { actually } from '../logging';
import { getPersistedParameters } from '../persistParameterContext';
import { getUrlParams } from '../url';

type Context = {
  authClient: MGMAuth;
  getUserInfo: () => Promise<OktaUser | null>;
  acquireToken: () => Promise<AccessToken | undefined>;
  acquireTransientToken: () => Promise<AccessToken | undefined>;
};

export const AuthContext = createContext<Context>({} as Context);

const NON_AUTO_REDIRECT_URLS: string[] = [
  '/identity/auth/login',
  '/identity/auth/mobile',
  '/identity/change-password',
  '/identity/external/authorize/marriott',
  '/identity/external/authentication/east',
];

export const AuthContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const redirectToApp = useAppRedirect();
  const params = getPersistedParameters();
  const { getContentByPath } = useContent();

  const { client_id = 'mgm_app_web' }: { client_id?: string } = params;
  const { isAllowOktaSignInWithRedirect, isCloseActiveSession } = getFeaturesByClientId(client_id as ClientId);
  const [loadFeatureFlags, setLoadFeatureFlags] = useState(!isCloseActiveSession);

  const localOverrides = useMemo(() => {
    return window.location.hostname === 'localhost'
      ? {
          identityAuthOrigin: `http://${window.location.host}/identity`,
          debugMode: true,
          logLevel: LogLevel.trace,
        }
      : {};
  }, []);

  const logLevel = process.env.REACT_APP_LOG_LEVEL
    ? (Number(process.env.REACT_APP_LOG_LEVEL) as LogLevel)
    : process.env.REACT_APP_ENVIRONMENT === 'prod'
    ? LogLevel.warn
    : LogLevel.log;

  const authConfig = useMemo(
    () => ({
      clientId: client_id as string,
      environment: (process.env.REACT_APP_ENVIRONMENT?.toUpperCase() ?? 'DEV') as Environment,
      appName: 'Identity Widget',
      logLevel: logLevel,
      allowOktaSignInWithRedirect: isAllowOktaSignInWithRedirect,
      ...(isAllowOktaSignInWithRedirect && { scopes: getThirdPartyScopesByClientId(client_id as ClientId) }),
      ...localOverrides,
    }),
    [client_id, isAllowOktaSignInWithRedirect, localOverrides, logLevel],
  );

  const authClient = useMemo(() => new MGMAuth(authConfig), [authConfig]);
  window.mgm = { ...window.mgm, authClient };

  const getUserInfo = useCallback(async () => {
    const activeSessionObj = await authClient.hasActiveMemberSession();
    if (activeSessionObj?.activeMemberSession && activeSessionObj?.session?.user) {
      const user = (await activeSessionObj.session.user()) as OktaUser;
      return user;
    }
    return null;
  }, [authClient]);

  const acquireToken = useCallback(async () => {
    const token = await authClient.acquireUserTokens();
    return token;
  }, [authClient]);

  const acquireTransientToken = useCallback(async () => {
    const isExistingSession = await authClient.hasActiveMemberSession();
    if (isExistingSession?.activeMemberSession) return acquireToken();

    const token = await authClient.acquireUserTokens(true);
    return token;
  }, [acquireToken, authClient]);

  const verifyPartnerExpiry = useCallback(() => {
    if (params.partner && params.tokenKey && !params.message?.includes(`${params.partner}_error`)) {
      const partnerExpiry = getContentByPath<string>(`signIn.partnerExpiry.${params.partner}`);
      const ignoreParams = ['partner', 'tokenKey', 'message'];

      if (partnerExpiry && parseInt(`${new Date().valueOf()}`) > parseInt(`${new Date(partnerExpiry).valueOf()}`)) {
        let search = '';
        for (const param in params) {
          if (!ignoreParams.includes(param)) {
            search += `&${param}=${params[param]}`;
          }
        }

        try {
          window.location.href = new URL(
            `${window.location.protocol}//${window.location.host}/identity?message=${params.partner}_error_expired${search}`,
          ).toString();

          return true;
        } catch (e: unknown) {
          actually.warn('could not parse partner expiry url');
        }
      }
    }

    return false;
  }, [getContentByPath, params]);

  // If the user is already logged in, bounce them back out.
  useEffect(() => {
    async function redirectIfLoggedIn() {
      try {
        if (isCloseActiveSession) {
          const { activeMemberSession } = await authClient.hasActiveMemberSession();
          activeMemberSession && (await authClient.signOut());
          setLoadFeatureFlags(true);
          return;
        }

        const token = await authClient.acquireUserTokens(true);

        if (token && !token.isTransient) {
          if (params.partner && params.tokenKey && token.mlife) {
            await linkAccountToPartner(params.partner as string, params.tokenKey as string, token.mlife);
          }

          /**
           * Get URL params for determining clientId
           */
          const urlParams = getUrlParams();

          if (isAllowOktaSignInWithRedirect) {
            const options: TokenParams = {};
            options.redirectUri = urlParams.redirectUri;
            options.codeChallenge = urlParams.codeChallenge;
            options.scopes = getThirdPartyScopesByClientId(urlParams.clientId as ClientId);
            options.state = urlParams.state;
            await authClient.getAuthClient().signInWithRedirect(options);
            return;
          }

          redirectToApp();
        }
      } catch (error) {
        console.error('[Auth Redirect] An error occurred while attempting to redirect you.');
      }
    }

    if (verifyPartnerExpiry()) return;

    if (
      !NON_AUTO_REDIRECT_URLS.includes(window.location.pathname) &&
      !params.message?.includes(`${params.partner}_error`)
    ) {
      redirectIfLoggedIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(
    () => ({ authClient, getUserInfo, acquireToken, acquireTransientToken }),
    [authClient, getUserInfo, acquireToken, acquireTransientToken],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {loadFeatureFlags && <FlagsProvider>{children}</FlagsProvider>}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext); // the custom hook
